import React, { useState, useEffect } from 'react';
import { Box, Typography, Modal, Menu, MenuItem, CircularProgress } from '@mui/material';
import CustomIconButton from './CustomIconButton';
import InputField from './InputField';
import AlertToast from './Alert';
import link from 'packages/novous_web/src/assets/icons/Link.svg';
import { API_ENDPOINTS } from 'packages/novous_web/src/api/endpoints';
import useAxios from 'packages/novous_web/src/api/useAxios';
import axiosInstance from 'packages/novous_web/src/api/apiClient';

interface ShareFootageModalProps {
  open: boolean;
  onClose: () => void;
  heading?: string;
  subheading?: string;
  videoFetchConfig?: {
    endpoint: string;
    method: 'GET' | 'POST';
    payload: any;
  };
  existingVideos?: string[];
  emailConfig?: {
    endpoint: string;
    payload: any;
  };
  cameraMapping?: Record<string, string>;
  onVideoCopied?: (url: string, cameraName: string) => void;
  onEmailSent?: (email: string) => void;
}

const ShareFootageModal: React.FC<ShareFootageModalProps> = ({
  open,
  onClose,
  heading = 'Share Footage',
  subheading = 'Enter an email address to share this Footage',
  videoFetchConfig,
  existingVideos = [],
  emailConfig,
  cameraMapping = {
    '01': 'ADAS',
    '02': 'CAM3',
    '03': 'CAM4',
    '04': 'DMS',
  },
  onVideoCopied,
  onEmailSent,
}) => {
  const [email, setEmail] = useState('');
  const [videos, setVideos] = useState<string[]>(existingVideos || []);
  const [loading, setLoading] = useState(false);
  const [copyLinkAnchorEl, setCopyLinkAnchorEl] = useState<null | HTMLElement>(null);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState<'error' | 'success' | 'warning' | 'info'>('error');

  const isCopyLinkMenuOpen = Boolean(copyLinkAnchorEl);

  const validateEmail = (email: string): boolean => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };
  const [userEmailValid, setUserEmailValid] = useState(false);

  useEffect(() => {
    setUserEmailValid(validateEmail(email));
  }, [email]);

  const {
    data: emailData,
    fetchData: fetchEmailData,
    loading: emailLoading,
  } = useAxios<any>(emailConfig?.endpoint || '', 'POST');

  const fetchVideos = async () => {
    if (!videoFetchConfig || videos.length > 0) return;

    setLoading(true);
    try {
      const response = await axiosInstance({
        url: videoFetchConfig.endpoint,
        method: videoFetchConfig.method,
        data: videoFetchConfig.payload,
        responseType: 'json',
      });

      const fetchedVideos = Array.isArray(response.data.data) ? response.data.data : [];
      setVideos(fetchedVideos);
    } catch (error) {
      console.error('Error fetching videos:', error);
      setAlertMessage('Failed to fetch video links');
      setAlertType('error');
      setAlertOpen(true);
    } finally {
      setLoading(false);
    }
  };
  console.info('Fetched videos:', videos);

  const getCameraType = (url: string) => {
    const fileName = url.split('/').pop()?.split('?')[0] || '';
    const segments = fileName.split('_');
    const cameraCode = segments[1] || 'Unknown';
    const cameraName = cameraMapping[cameraCode] || 'Unknown';
    return { cameraId: cameraCode, cameraName, playBackUrl: url };
  };

  const handleOpenCopyLinkMenu = (anchor: HTMLElement) => {
    setCopyLinkAnchorEl(anchor);
  };

  const handleCloseCopyLinkMenu = () => setCopyLinkAnchorEl(null);

  const handleCopySpecificVideo = (url: string) => {
    const cameraInfo = getCameraType(url);
    navigator.clipboard
      .writeText(url)
      .then(() => {
        setAlertMessage('Video link copied to clipboard');
        setAlertType('success');
        setAlertOpen(true);
        onVideoCopied?.(url, cameraInfo.cameraName);
      })
      .catch((err) => {
        console.error('Failed to copy video link: ', err);
        setAlertMessage('Failed to copy video link');
        setAlertType('error');
        setAlertOpen(true);
      })
      .finally(() => {
        handleCloseCopyLinkMenu();
      });
  };

  const handleEmailClose = () => {
    setEmail('');
    onClose();
  };

  const handleSubmit = async () => {
    if (!userEmailValid) {
      setAlertMessage('Please enter a valid email address');
      setAlertType('error');
      setAlertOpen(true);
      return;
    }

    if (emailConfig) {
      await fetchEmailData({
        ...emailConfig.payload,
        emailId: email,
      });
    }

    setEmail('');
    onEmailSent?.(email);
  };

  useEffect(() => {
    let closeTimer: number | undefined;
    if (emailData) {
      if (emailData.status === 200) {
        setAlertMessage('Email sent successfully');
        setAlertType('success');
        setAlertOpen(true);
        closeTimer = window.setTimeout(() => {
          handleEmailClose();
        }, 1000);
      } else {
        setAlertMessage('Failed to send email');
        setAlertType('error');
        setAlertOpen(true);
      }
    }
    return () => {
      if (closeTimer) window.clearTimeout(closeTimer);
    };
  }, [emailData]);

  useEffect(() => {
    if (open && existingVideos && existingVideos.length > 0) {
      setVideos(existingVideos);
    } else {
      setVideos([]);
      setCopyLinkAnchorEl(null);
    }
  }, [open, existingVideos]);

  useEffect(() => {
    if (open && videoFetchConfig && existingVideos.length === 0) {
      fetchVideos();
    }
  }, [open, videoFetchConfig]);

  return (
    <>
      <Modal open={open} onClose={handleEmailClose}>
        <Box
          sx={{
            position: 'absolute' as const,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90%', sm: 500 },
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <Typography variant="h6">{heading}</Typography>
            <CustomIconButton
              text="Copy Link"
              icon={<img src={link} alt="link" width={24} height={24} />}
              background="transparent"
              outline={false}
              padding="0px"
              size={16}
              variant="outlined"
              onClick={(e: any) => handleOpenCopyLinkMenu(e.currentTarget as HTMLElement)}
            />
            <Menu
              anchorEl={copyLinkAnchorEl}
              open={isCopyLinkMenuOpen}
              onClose={handleCloseCopyLinkMenu}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              {videos.length > 0 ? (
                videos.map((url: string, idx: number) => {
                  const cameraInfo = getCameraType(url);
                  const cameraSuffix =
                    cameraInfo?.cameraName && cameraInfo.cameraName !== 'Unknown'
                      ? ` - ${cameraInfo.cameraName}`
                      : '';
                  return (
                    <MenuItem key={`${url}-${idx}`} onClick={() => handleCopySpecificVideo(url)}>
                      {`Copy Link ${idx + 1}${cameraSuffix}`}
                    </MenuItem>
                  );
                })
              ) : loading ? (
                <MenuItem disabled>Loading video links...</MenuItem>
              ) : (
                <MenuItem disabled>No video links available</MenuItem>
              )}
            </Menu>
          </Box>
          <Typography variant="button" fontWeight={400} className="mb-1" color="secondary">
            {subheading}
          </Typography>
          <div className="mt-4">
            <InputField
              label="Email Address"
              name="email"
              value={email}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
              placeholder="Enter email address"
              status={email.length === 0 ? undefined : userEmailValid ? 'success' : 'error'}
              isDisabled={false}
            />
          </div>
          <div className="flex w-full mt-8 justify-between">
            <CustomIconButton
              text="Cancel"
              background="transparent"
              outline={true}
              padding="0px"
              size={16}
              variant="outlined"
              onClick={handleEmailClose}
            />
            <div>
              <CustomIconButton
                text={emailLoading ? 'Sending…' : 'Send'}
                padding="0px"
                outline={false}
                size={16}
                isDisabled={!userEmailValid || emailLoading}
                variant="contained"
                onClick={handleSubmit}
              />
            </div>
          </div>
        </Box>
      </Modal>
      <AlertToast
        message={alertMessage}
        type={alertType}
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
      />
    </>
  );
};

export default ShareFootageModal;
